
import { Component, Vue } from 'vue-property-decorator'

import BacLoggedInHeader from '@/templates/BacLoggedInHeader.vue'
import BacButton from '@/components/BacButton.vue'
import BacModal from '@/components/BacModal.vue'
import BacInput from '@/components/BacInput.vue'
import BacMultiselectWrapper from '@/components/BacMultiselectWrapper.vue'

import { Term } from '../../lib/models/Term'

import Editor from '@tinymce/tinymce-vue'
import Multiselect from 'vue-multiselect'

import sanitizeHtml from 'sanitize-html'
import { Rally } from '../../lib/models/Rally'
import { RallyRound } from '../../lib/models/RallyRound'

@Component({
  components: {
    BacLoggedInHeader,
    BacButton,
    BacModal,
    BacInput,
    BacMultiselectWrapper,
    Multiselect,
    Editor
  }
})
export default class Teams extends Vue {
  terms: Term[] = []
  selectedTerm: Term = new Term()

  rallies: Rally[] = []
  selectedRallies = []

  rallyRounds: RallyRound[] = []
  selectedRallyRounds = []

  headers = [
    { text: 'Name', value: 'name', width: 20 },
    { text: 'Leistungsbeschreibung', value: 'term', width: 500 },
    { text: 'Rallys', value: 'rallies', width: 50 },
    { text: 'Rallyrunden', value: 'rallyRounds', width: 50 },
    { text: 'Aktionen', value: 'actions', width: 50 }
  ]

  renderEditor = false

  get ralliesAsMultiselect () {
    return this.rallies.reverse().map(rally => {
      return { name: rally.name, id: rally.id }
    })
  }

  get rallyRoundsAsMultiselect () {
    return this.rallyRounds.reverse().map(rallyRound => {
      const rally = this.rallies.find(rally => {
        return rally.id === rallyRound.rallyId
      })
      if (rally) {
        return { name: `${rally.name} ${rallyRound.name}`, id: rallyRound.id }
      } else {
        throw new Error("Couldn't find matching Rally for RallyRound, this should never happen!")
      }
    })
  }

  get items () {
    return this.terms
  }

  mounted () {
    this.loadTerms()
    this.loadRallies()
    this.loadRallyRounds()
  }

  resetSelectedTerm () {
    this.selectedTerm = new Term()
  }

  async loadTerms () {
    this.terms = await Term.getAll({})
  }

  async loadRallies () {
    this.rallies = await Rally.getAll({})
  }

  async loadRallyRounds () {
    this.rallyRounds = await RallyRound.getAll({})
  }

  async saveCreateTerm () {
    let termId = this.selectedTerm.id
    if (this.selectedTerm.id) {
      await this.selectedTerm.update()
    } else {
      const resData = await this.selectedTerm.create()
      termId = resData.term.id
    }

    // reset all current rallies set to this term id
    this.rallies
      .filter(rally => {
        return rally.termId === termId
      })
      .forEach(async rally => {
        rally.termId = null
        await rally.update()
      })

    // reset all current rally rounds set to this term id
    this.rallyRounds
      .filter(rallyRound => {
        return rallyRound.termId === termId
      })
      .forEach(async rallyRound => {
        rallyRound.termId = null
        await rallyRound.update()
      })

    // set term id of rallies
    const selectedRallies: Rally[] = (this.selectedRallies as any).map((rally: Rally) => {
      return this.rallies.find(realRallyObject => {
        return rally.id === realRallyObject.id
      })
    })
    await Promise.all(selectedRallies.map(async rally => {
      rally.termId = termId
      await rally.update()
    }))

    // set term id of rally rounds
    const selectedRallyRounds: RallyRound[] = (this.selectedRallyRounds as any).map((rallyRound: RallyRound) => {
      return this.rallyRounds.find(realRallyRoundObject => {
        return rallyRound.id === realRallyRoundObject.id
      })
    })
    await Promise.all(selectedRallyRounds.map(async rallyRound => {
      rallyRound.termId = termId
      await rallyRound.update()
    }))

    await this.loadRallies()
    await this.loadRallyRounds()
    this.loadTerms()
  }

  async deleteTerm () {
    if (this.selectedTerm.id) {
      await Term.delete(this.selectedTerm.id)
      this.loadTerms()
    }
  }

  sanitizedTrimmedPreview (rawHtml: string) {
    const sanitizedHtml = sanitizeHtml(rawHtml)

    let allowedChars = 100
    let positionOfHtmlCloseAfter100 = sanitizedHtml.substring(0, 100).search(/<\/.*>/)
    if (positionOfHtmlCloseAfter100 > 300) {
      // if it's too long to next html close, limit to 100 chars or html close before that
      positionOfHtmlCloseAfter100 = 0
      if (sanitizedHtml.indexOf('<') > 30) {
        allowedChars = sanitizedHtml.indexOf('<')
      }
    }

    return sanitizedHtml.substring(0, allowedChars + positionOfHtmlCloseAfter100 - 10)
  }

  setTermRelations (termId: number) {
    const alreadySelectedRallyRounds = this.rallyRounds.filter(rallyRound => {
      return rallyRound.termId === termId
    });
    (this.selectedRallyRounds as any) = alreadySelectedRallyRounds.map(rallyRound => {
      const rally = this.rallies.find(rally => {
        return rally.id === rallyRound.rallyId
      })
      if (rally) {
        return { name: `${rally.name} ${rallyRound.name}`, id: rallyRound.id }
      } else {
        throw new Error("Couldn't find matching Rally for RallyRound, this should never happen!")
      }
    })

    const alreadySelectedRallies = this.rallies.filter(rally => {
      return rally.termId === termId
    });
    (this.selectedRallies as any) = alreadySelectedRallies.map(rally => {
      return { id: rally.id, name: rally.name }
    })
  }

  getRallyNamesForItem (termId: number) {
    const ralliesForThisTerm = this.rallies.filter(rally => {
      return rally.termId === termId
    })
    let ralliesString = ''
    ralliesForThisTerm.forEach(rally => {
      ralliesString += rally.name + '\n'
    })
    return ralliesString
  }

  getRallyRoundNamesForItem (termId: number) {
    const rallyRoundsForThisTerm = this.rallyRounds.filter(rallyRound => {
      return rallyRound.termId === termId
    })
    let rallyRoundsString = ''
    rallyRoundsForThisTerm.forEach(rallyRound => {
      const rally = this.rallies.find(rally => rally.id === rallyRound.rallyId)
      if (!rally) {
        throw new Error('Couldnt find rally for rally round, this should never happen!')
      }
      rallyRoundsString += `${rally.name} ${rallyRound.name} \n`
    })
    return rallyRoundsString
  }

  // TinyMCE doesn't work when element is display none for some reason, we reload it when the create or edit button is clicked
  // https://github.com/tinymce/tinymce-vue/issues/112
  // https://michaelnthiessen.com/force-re-render/
  forceRerenderEditor () {
    // Remove my-component from the DOM
    this.renderEditor = false

    this.$nextTick(() => {
      // Add the component back in
      this.renderEditor = true
    })
  }
}

